<template>
  <fts-design-modal
    append-to-body
    width="768px"
    desc="automation action designer"
    class="fts-automation-action-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-title">
        {{ title }}
        <fts-release-badge :stage="releaseStage" class="fts-align-top" v-if="releaseStage" />
      </div>
    </template>
    <!-- body -->
    <template #body>
      <!-- spinner -->
      <div class="design-modal-spinner" v-if="loading">
        <fts-spinner />
      </div>

      <template v-else-if="!selectedAction">
        <div class="automation-action-menu-container">
          <div class="automation-action-menu">
            <div
              v-for="item in groupSelections"
              :key="item.id"
              class="menu-item"
              :class="{ 'menu-item--active': item === selectedGroup }"
              @click="selectedGroup = item"
            >
              <div class="menu-item-icon">
                <img :src="inistateLogo" alt="app icon" />
              </div>
              <div class="menu-item-name">
                {{ item.name }}
              </div>
            </div>
          </div>
          <div
            class="automation-action-menu automation-action-menu--action fts-scrollbar fts-scroll-overlay"
          >
            <template v-if="selectedGroup && selectedGroup.id === 'basic'">
              <div
                v-for="item in actionSelections"
                :key="item.id"
                class="menu-item"
                @click="selectAction(item)"
              >
                <div>
                  <div class="menu-item-name">
                    {{ item.name }}
                    <fts-release-badge :stage="item.releaseStage" class="fts-align-top" v-if="item.releaseStage" />
                  </div>
                  <div class="menu-item-description">
                    {{ item.description }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div
                v-for="item in selectedGroup.list"
                :key="item.id"
                class="menu-item"
                @click="selectPluginAction(selectedGroup, item)"
              >
                <div>
                  <div class="menu-item-name">
                    {{ item.name }}
                  </div>
                  <div class="menu-item-description">
                    {{ item.description }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <!-- error -->
        <div
          class="automation-action-menu-error"
          v-if="$v.formData.type && $v.formData.type.$error"
        >
          To continue, please select an action.
        </div>
      </template>
      <!-- plugin -->
      <div v-else-if="formData.pluginType">
        <div v-if="formData.payload">
          <div v-for="field in selectedAction.form" :key="field.fieldName">
            <!-- contenteditable -->
            <div v-if="field.type == 'contenteditable'">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: field.fieldName,
                  required: field.required,
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <div
                  class="action-variable"
                  slot="formElement"
                  slot-scope="{ formData, item, onInput, onBlur }"
                >
                  <i
                    class="action-variable-icon"
                    :class="informationIcon(formData.variableType)"
                    v-if="selectedAction.variable"
                  ></i>

                  <div class="action-variable-wrapper">
                    <fts-automation-contenteditable
                      v-model="formData[item.fieldName]"
                      class="action-variable-control"
                      @input="onInput"
                      @blur="onBlur"
                    />
                    <div
                      class="action-variable-tag"
                      v-if="
                        formData.moduleId ||
                        formData.activityId ||
                        formData.informationId
                      "
                    >
                      <fts-automation-badge
                        :module-id="formData.moduleId"
                        :activity-id="formData.activityId"
                        :informationId="formData.informationId"
                        :isList="formData.isList"
                        :isGroup="formData.isGroup"
                        class="action-variable-tag__badge"
                      />
                    </div>
                  </div>
                </div>
              </fts-form-element>
            </div>
            <fts-field-design
              :variables="variables"
              :item="field"
              v-model="formData.payload[field.fieldName]"
              :validator="$v.formData.payload"
              :expanded="formData[`${field.fieldName}Expand`]"
              @expand="expandFields(field.fieldName)"
              @formula:change="validateFormula"
              v-else
            />
          </div>
        </div>
        <!-- validate message -->
        <div class="automation-validate-message" v-if="validating">
          validating...
        </div>
        <div class="automation-validate-message automation-validate-message--error" v-else-if="errorMessage">
          {{errorMessage}}
        </div>
      </div>
      <!-- form -->
      <div v-else>
        <div v-for="field in selectedAction.form" :key="field.id">
          <!-- field -->
          <div v-if="field.type === 'field'">
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 32,
                displayName: field.name,
                fieldName: field.id,
                required: field.required,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <fts-formula
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                v-model="formData[item.fieldName]"
                :variables="variables"
                @change="validateFormula"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- formula -->
          <div v-else-if="field.type === 'formula'">
            <div
              class="design-modal-label-group value-label-group"
              :class="{ multiple: formData[field.id + 'Expand'] }"
            >
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{
                  field.name
                }}</fts-form-label>
              </div>
              <div class="design-modal-label-group-r" v-if="field.expandable">
                <fts-btn
                  sm
                  outline
                  text-transform-init
                  @click="expandCollapseValue(field.id + 'Expand')"
                >
                  {{ formData[field.id + "Expand"] ? "Collapse" : "Expand" }}
                  Property
                </fts-btn>
              </div>
            </div>
            <!-- multiple -->
            <div
              class="value-property-container"
              v-if="formData[field.id + 'Expand']"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'fields',
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <template #formElement="{ formData, item }">
                  <div
                    v-for="property in formData[item.fieldName]"
                    :key="property.id"
                    class="value-property-wrapper"
                  >
                    <!-- key -->
                    <div class="value-property-key">
                      <input
                        type="text"
                        class="fts-form-control"
                        v-model="property.key"
                        v-if="formData.variableType == 37"
                      />

                      <v-select
                        append-to-body
                        label="displayName"
                        :options="fieldOptions"
                        v-model="property.key"
                        :text="getName(property)"
                        :reduce="(x) => x.fieldName"
                        :filter="filterOptions"
                        :keys="fieldKeys"
                        comfort-design
                        @input="fieldValidatorOnInput(property.id, 'key')"
                        @search:blur="fieldValidatorOnInput(property.id, 'key')"
                        v-else
                      >
                        <fts-label-group 
                          slot="selected-option" 
                          slot-scope="{ text }" 
                          :item="{
                            icon: getIconByFieldName(property.key, fieldOptions),
                            displayName: text
                          }"
                          information 
                        />

                        <fts-label-group 
                          slot="option" 
                          slot-scope="{ option }" 
                          :item="option" 
                          information 
                        />
                      </v-select>
                      <div
                        class="design-modal-form-error"
                        v-if="fieldValidatorInvalid(property.id, 'key')"
                      >
                        Field is required.
                      </div>
                    </div>
                    <!-- value -->
                    <fts-formula
                      v-model="property.value"
                      :variables="variables"
                      class="value-property-value"
                      @change="validateFormula"
                    />
                    <!-- action -->
                    <fts-btn icon text @click="removeValue(property)">
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </fts-btn>
                  </div>
                  <!-- action add -->
                  <fts-btn
                    sm
                    outline
                    color="primary"
                    text-transform-init
                    @click="addValue"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" /> Add Value
                  </fts-btn>
                </template>
              </fts-form-element>
            </div>
            <!-- single -->
            <div v-else>
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: field.id,
                  required: field.required,
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <fts-formula
                  slot="formElement"
                  slot-scope="{ formData, item, onInput, onBlur }"
                  v-model="formData[item.fieldName]"
                  :variables="variables"
                  @change="validateFormula"
                  @input="onInput"
                  @blur="onBlur"
                />
              </fts-form-element>
            </div>
          </div>
          <!-- declared sync trigger fields (Mechanism B1, formsync-declared-triggers-proposal.md §3) -->
          <div v-else-if="field.type === 'triggerFields'" class="trigger-fields-group">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <div class="trigger-fields-hint">
              On forms, this logic re-runs only when one of these fields changes; other edits
              stay local until then. Leave empty to keep syncing on every edit.
            </div>
            <div
              v-for="(token, index) in triggerFieldRows"
              :key="'triggerField' + index"
              class="trigger-fields-row"
            >
              <fts-formula
                :formula="token"
                :variables="variables"
                class="trigger-fields-row__formula"
                @input="setTriggerField(index, $event)"
              />
              <fts-btn icon text @click="removeTriggerField(index)">
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
            <fts-btn sm outline color="primary" text-transform-init @click="addTriggerField">
              <font-awesome-icon :icon="['fas', 'plus']" /> Add Field
            </fts-btn>
            <!-- D-8: the effective combined set the form will actually sync on -->
            <div class="trigger-fields-combined" v-if="combinedSyncFields.length">
              Form syncs on:
              <span
                class="trigger-fields-combined__chip"
                v-for="item in combinedSyncFields"
                :key="item.label"
                :title="item.reasons"
              >{{ item.label }}</span>
            </div>
          </div>
          <!-- module -->
          <div v-else-if="field.type == 'module'">
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Module',
                fieldName: 'module',
                required: field.required,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="moduleOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @option:selected="updateModule"
                @option:clear="updateModule"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="moduleOptions"
                  emoji
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="moduleOptions"
                  emoji
                />
              </fts-select>
            </fts-form-element>
          </div>
          <!-- logic -->
          <div v-else-if="field.type == 'logic'">
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Logic',
                fieldName: 'logic',
                required: field.required,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="logicOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="logicOptions"
                  emoji
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="logicOptions"
                  emoji
                />
              </fts-select>
            </fts-form-element>
          </div>
          <!-- acitivity -->
          <div v-else-if="field.type == 'activity'">
            <!-- select -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Activity',
                fieldName: field.id,
                required: field.required,
              }"
              :validator="$v.formData"
              v-if="selectedModule"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="activityOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @option:selected="updateActivity"
                @option:clear="updateActivity"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :icon="getStandardActivityIcon(option.id)"
                  :item-id="option.id"
                  :list="activityOptions"
                  :emoji="!getStandardActivityIcon(option.id)"
                  system-default
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :icon="getStandardActivityIcon(option.id)"
                  :item-id="option.id"
                  :list="activityOptions"
                  :emoji="!getStandardActivityIcon(option.id)"
                  system-default
                />
              </fts-select>
            </fts-form-element>
            <!-- formula -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 32,
                fieldName: 'entry',
                required: selectedActivity && selectedActivity.id != 'create',
              }"
              :validator="$v.formData"
              comfort-design
              v-if="selectedActivity && selectedActivity.id != 'create'"
            >
              <fts-formula
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                v-model="formData[item.fieldName]"
                :variables="variables"
                @change="validateFormula"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- template -->
          <div v-else-if="field.type == 'template'">
            <!-- select -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Template',
                fieldName: field.id,
                required: field.required,
              }"
              :validator="$v.formData"
              v-if="selectedModule"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="templateOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @option:selected="updateTemplate"
                @option:clear="updateTemplate"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="templateOptions"
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="templateOptions"
                />
              </fts-select>
            </fts-form-element>
            <!-- formula -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 32,
                fieldName: 'entry',
                required: (selectedActivity && selectedActivity.id != 'create') || selectedTemplate,
              }"
              :validator="$v.formData"
              comfort-design
              v-if="(selectedActivity && selectedActivity.id != 'create') || selectedTemplate"
            >
              <fts-formula
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                v-model="formData[item.fieldName]"
                :variables="variables"
                @change="validateFormula"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- form -->
          <div v-else-if="field.type == 'form'">
            <template v-if="selectedActivity && selectedActivity.hasForm">
              <div
                class="design-modal-label-group value-label-group"
                :class="{ multiple: formData[field.id + 'Expand'] }"
              >
                <div class="design-modal-label-group-l">
                  <fts-form-label placement="top">{{
                    field.name
                  }}</fts-form-label>
                </div>
                <div class="design-modal-label-group-r" v-if="field.expandable">
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    @click="expandCollapseValue(field.id + 'Expand')"
                  >
                    {{ formData[field.id + "Expand"] ? "Collapse" : "Expand" }}
                    Property
                  </fts-btn>
                </div>
              </div>
              <!-- multiple -->
              <div
                class="value-property-container"
                v-if="formData[field.id + 'Expand']"
              >
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    fieldName: 'fields',
                    label: false,
                  }"
                  :validator="$v.formData"
                  comfort-design
                >
                  <template #formElement="{ formData, item }">
                    <div
                      v-for="property in formData[item.fieldName]"
                      :key="property.id"
                      class="value-property-wrapper"
                    >
                      <!-- key -->
                      <div class="value-property-key">
                        <v-select
                          append-to-body
                          label="displayName"
                          :options="fieldOptions"
                          v-model="property.key"
                          :text="getName(property)"
                          :reduce="(x) => x.fieldName"
                          :filter="filterOptions"
                          :keys="fieldKeys"
                          comfort-design
                          @input="fieldValidatorOnInput(property.id, 'key')"
                          @search:blur="
                            fieldValidatorOnInput(property.id, 'key')
                          "
                        >
                          <fts-label-group 
                            slot="selected-option" 
                            slot-scope="{ text }" 
                            :item="{
                              icon: getIconByFieldName(property.key, fieldOptions),
                              displayName: text
                            }"
                            information 
                          />

                          <fts-label-group 
                            slot="option" 
                            slot-scope="{ option }" 
                            :item="option" 
                            information 
                          />
                        </v-select>
                        <div
                          class="design-modal-form-error"
                          v-if="fieldValidatorInvalid(property.id, 'key')"
                        >
                          Field is required.
                        </div>
                      </div>
                      <!-- value -->
                      <fts-formula
                        v-model="property.value"
                        :variables="variables"
                        class="value-property-value"
                        @change="validateFormula"
                      />
                      <!-- action -->
                      <fts-btn icon text @click="removeValue(property)">
                        <font-awesome-icon :icon="['far', 'trash-alt']" />
                      </fts-btn>
                    </div>
                    <!-- action add -->
                    <fts-btn
                      sm
                      outline
                      color="primary"
                      text-transform-init
                      @click="addValue"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" /> Add Value
                    </fts-btn>
                  </template>
                </fts-form-element>
              </div>
              <!-- single -->
              <div v-else>
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    fieldName: field.id,
                    required: field.required,
                    label: false,
                  }"
                  :validator="$v.formData"
                  comfort-design
                >
                  <fts-formula
                    slot="formElement"
                    slot-scope="{ formData, item, onInput, onBlur }"
                    v-model="formData[item.fieldName]"
                    :variables="variables"
                    @change="validateFormula"
                    @input="onInput"
                    @blur="onBlur"
                  />
                </fts-form-element>
              </div>
            </template>
          </div>
          <!-- filter -->
          <div
            class="design-modal-form-group"
            v-else-if="field.type == 'filter' && selectedModule"
          >
            <div class="design-modal-form-label">Filter</div>
            <fts-query-designer
              :states="selectedModule.states"
              :information-source="querySelection"
              :variables="variables"
              :filter="formData.filter"
            />
          </div>
          <!-- Find / Find all (automation-blocks-simplification P1 + P3): Result, Order by, Limit, When none found -->
          <div v-else-if="field.type == 'findMode'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <!-- commit from updateValue, never from input: the vendored select emits input:null on
                 every search keystroke (see the fill-scope picker below) -->
            <v-select
              append-to-body
              :options="findModeOptions"
              label="label"
              :value="findModeOptions.find((x) => x.value === (formData.mode || 'rows'))"
              :text="optionText(findModeOptions.find((x) => x.value === (formData.mode || 'rows')))"
              :clearable="false"
              comfort-design
              @updateValue="(o) => o && setFindMode(o.value)"
            />
          </div>
          <div v-else-if="field.type == 'findSort' && selectedModule && formData.mode !== 'count'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <div class="find-sort-row">
              <v-select
                append-to-body
                class="find-sort-field"
                :options="findSortFields"
                label="displayName"
                :value="findSortFields.find((x) => x.id === findSortFieldId) || null"
                :text="optionText(findSortFields.find((x) => x.id === findSortFieldId), 'displayName')"
                placeholder="Last updated first"
                comfort-design
                @updateValue="(o) => o && setFindSort(o.id, findSortDirection)"
                @option:cleared="setFindSort(null)"
              />
              <v-select
                v-if="findSortFieldId"
                append-to-body
                class="find-sort-direction"
                :options="findSortDirections"
                label="label"
                :value="findSortDirections.find((x) => x.value === findSortDirection)"
                :text="optionText(findSortDirections.find((x) => x.value === findSortDirection))"
                :clearable="false"
                comfort-design
                @updateValue="(o) => o && setFindSort(findSortFieldId, o.value)"
              />
            </div>
          </div>
          <div v-else-if="field.type == 'findLimit' && formData.mode !== 'count'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <input
              type="number"
              class="fts-form-control"
              min="1"
              max="1000"
              placeholder="200"
              :value="formData.limit"
              @input="(e) => setFindLimit(e.target.value)"
            />
          </div>
          <!-- Table rows (automation-blocks-simplification P2): Action, Table, Rows where, Rows, Set columns -->
          <div v-else-if="field.type == 'tableRowsMode'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <v-select
              append-to-body
              :options="tableRowsModeOptions"
              label="label"
              :value="tableRowsModeOptions.find((x) => x.value === tableRowsMode)"
              :text="optionText(tableRowsModeOptions.find((x) => x.value === tableRowsMode))"
              :clearable="false"
              comfort-design
              @updateValue="(o) => o && setTableRowsMode(o.value)"
            />
          </div>
          <div v-else-if="field.type == 'tableRowsTable' && tableRowsUses('table')">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <fts-formula v-model="formData.table" :variables="variables" />
            <div class="table-rows-hint">The table field, e.g. {{ "{" + "{entry.details}" + "}" }}</div>
          </div>
          <div v-else-if="field.type == 'tableRowsWhere' && tableRowsUses('where')">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <fts-formula v-model="formData.where" :variables="variables" />
            <div class="table-rows-hint">
              {{ tableRowsMode === "upsert" ? "The key: it must match one row at most" : "A condition on the table's columns" }},
              e.g. {{ "{" + "{entry.details.product}" + "}" }} == {{ "{" + "{form.product}" + "}" }}
            </div>
          </div>
          <div v-else-if="field.type == 'tableRowsMatch' && tableRowsUses('match')">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <v-select
              append-to-body
              :options="tableRowsMatchOptions"
              label="label"
              :value="tableRowsMatchOptions.find((x) => x.value === (formData.match || 'first'))"
              :text="optionText(tableRowsMatchOptions.find((x) => x.value === (formData.match || 'first')))"
              :clearable="false"
              comfort-design
              @updateValue="(o) => o && setTableRowsMatch(o.value)"
            />
          </div>
          <div v-else-if="field.type == 'tableRowsFields' && tableRowsUses('fields')">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
              <div class="design-modal-label-group-r">
                <fts-btn sm outline text-transform-init @click="addTableRowsField">Add column</fts-btn>
              </div>
            </div>
            <div
              class="table-rows-field"
              v-for="(item, i) in formData.fields || []"
              :key="i"
            >
              <input
                type="text"
                class="fts-form-control table-rows-column"
                placeholder="Column name"
                :value="item.column"
                @input="(e) => setTableRowsField(i, 'column', e.target.value)"
              />
              <fts-formula
                class="table-rows-value"
                :formula="item.value"
                :variables="variables"
                @updateFormula="(v) => setTableRowsField(i, 'value', v)"
              />
              <fts-btn sm outline text-transform-init @click="removeTableRowsField(i)">Remove</fts-btn>
            </div>
          </div>
          <div
            v-else-if="field.type == 'whenNone'
              && !(selectedAction.id == 'tableRows' && !tableRowsUses('whenNone'))"
          >
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <v-select
              append-to-body
              :options="whenNoneOptions"
              label="label"
              :value="whenNoneOptions.find((x) => x.value === (formData.whenNone || 'continue'))"
              :text="optionText(whenNoneOptions.find((x) => x.value === (formData.whenNone || 'continue')))"
              :clearable="false"
              comfort-design
              @updateValue="(o) => o && setWhenNone(o.value)"
            />
            <div v-if="formData.whenNone === 'error'" class="when-none-message">
              <fts-form-label placement="top">Error message</fts-form-label>
              <fts-formula v-model="formData.whenNoneMessage" :variables="variables" />
            </div>
          </div>
          <!-- teammate: plain multiline instruction (literal text, NOT a formula) -->
          <div v-else-if="field.type == 'textarea'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: field.id,
                required: field.required,
                label: false,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <textarea
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                class="fts-form-control"
                rows="4"
                v-model="formData[item.fieldName]"
                @input="onInput"
                @blur="onBlur"
              ></textarea>
            </fts-form-element>
          </div>
          <!-- teammate: seniority tier (server maps to a model; explicit Model wins) -->
          <div v-else-if="field.type == 'senioritySelect'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <v-select
              append-to-body
              :options="seniorityOptions"
              label="label"
              :reduce="(x) => x.value"
              :value="formData[field.id] || 'junior'"
              :text="optionText(seniorityOptions.find((x) => x.value === (formData[field.id] || 'junior')))"
              :clearable="false"
              comfort-design
              @input="(v) => $set(formData, field.id, v)"
            />
          </div>
          <!-- teammate: plain text (e.g. optional model override) -->
          <div v-else-if="field.type == 'text'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: field.id,
                required: field.required,
                label: false,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <input
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                type="text"
                class="fts-form-control"
                v-model="formData[item.fieldName]"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- teammate: plain number (e.g. timeout, clamped server-side) -->
          <div v-else-if="field.type == 'numberInput'">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <input
              type="number"
              class="fts-form-control"
              v-model.number="formData[field.id]"
            />
          </div>
          <!-- teammate: fill scope — which fields GNey may fill when a FORM trigger runs it -->
          <div v-else-if="field.type == 'fillScope'" class="teammate-fill-scope">
            <div class="design-modal-label-group">
              <div class="design-modal-label-group-l">
                <fts-form-label placement="top">{{ field.name }}</fts-form-label>
              </div>
            </div>
            <div class="teammate-fill-scope-hint">
              Applies when this action runs from a form trigger: GNey proposes values for the
              form's editable fields, except the ones excluded here. Computed, read-only,
              hidden and file fields are never filled.
            </div>
            <v-select
              :options="['All fields', 'All except…']"
              :value="fillScopeMode === 'except' ? 'All except…' : 'All fields'"
              :text="fillScopeMode === 'except' ? 'All except…' : 'All fields'"
              :clearable="false"
              comfort-design
              @updateValue="setFillScopeMode($event === 'All except…' ? 'except' : 'all')"
            />
            <!-- Commit from updateValue, never from input (same mechanism as SS05974 in
                 FTSFormScript): the vendored select's ajax mixin emits input:null on every
                 search keystroke - a single-select free-text convention - so listening to
                 input dropped every excluded field as soon as the user typed to find the
                 next one. Picks and removals arrive through option:selected /
                 option:deselected, which fire even when the select mutates its bound array
                 in place without emitting a value. -->
            <v-select
              v-if="fillScopeMode === 'except'"
              multiple
              append-to-body
              label="displayName"
              class="teammate-fill-scope-fields"
              :options="fillScopeOptions"
              :reduce="reduceFillScopeField"
              :value="fillScopeFields"
              comfort-design
              @updateValue="setFillScopeFields($event)"
              @option:selected="addFillScopeField($event)"
              @option:deselected="removeFillScopeField($event)"
              @option:cleared="setFillScopeFields([])"
            />
          </div>
          <!-- contenteditable -->
          <div v-else-if="field.type == 'contenteditable'">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: field.id,
                required: field.required,
                label: false,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <div
                class="action-variable"
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
              >
                <i
                  class="action-variable-icon"
                  :class="informationIcon(formData.variableType)"
                  v-if="selectedAction.variable"
                ></i>

                <div class="action-variable-wrapper">
                  <fts-automation-contenteditable
                    v-model="formData[item.fieldName]"
                    class="action-variable-control"
                    @input="onInput"
                    @blur="onBlur"
                  />
                  <div
                    class="action-variable-tag"
                    v-if="
                      formData.moduleId ||
                      formData.activityId ||
                      formData.informationId
                    "
                  >
                    <fts-automation-badge
                      :module-id="formData.moduleId"
                      :activity-id="formData.activityId"
                      :informationId="formData.informationId"
                      :isList="formData.isList"
                      :isGroup="formData.isGroup"
                      class="action-variable-tag__badge"
                    />
                  </div>
                </div>
              </div>
            </fts-form-element>
          </div>
        </div>
        <!-- <fts-automation-scoped-variables :variables="variables" /> -->
        <!-- validate message -->
        <div class="automation-validate-message" v-if="validating">
          validating...
        </div>
        <div class="automation-validate-message automation-validate-message--error" v-else-if="errorMessage">
          {{errorMessage}}
        </div>
      </div>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose"> Cancel </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
Vue.use(Vuelidate);

import changesets from "diff-json";
import vSelect from "../../vue-select";

import commons from "../../commons/commons";
import { ReleaseStage } from '../../commons/enum';
import queryLogic from "../../commons/queryLogic";
import formDesign from '../../commons/formDesign';
import { authorizationStore } from '../../store/authorizationStore';
import { automationDesignStore } from "../../store/automationDesignStore";

import FTSDesignModal from "../FTSDesignModal";
import FTSBtn from "../../elements/FTSBtn";
import FTSFormLabel from "../../elements/FTSFormLabel";
import FTSFormElement from "../../elements/FTSFormElement";
import FTSSelect from "../../elements/FTSSelect";
import FTSLabelGroup from "../../elements/FTSLabelGroup";
import automationBlocks from "../../commons/automationBlocks";
import FTSFormula from "../FTSFormula";
import FTSQueryDesigner from "../../design/FTSQueryDesigner";
import FTSAutomationScopedVariables from "./FTSAutomationScopedVariables";
import FTSAutomationContenteditable from "./FTSAutomationContenteditable";
import FTSAutomationBadge from "./FTSAutomationBadge";
import FTSSpinner from "../../elements/FTSSpinner";
import FTSFieldDesign from "../field/FTSFieldDesign";

/* SS04314: Username is a system field of a user-collection entry. It is not one of the
   module's own fields and no form design holds it, so a picker built from those never offered
   it. The server already handles it: a doActivity field keyed "username" writes the entry's
   Username, and {{entry.username}} resolves for a profile module (VariableSchemaCollection).
   Offered only where an entry's own values are set: the create/edit forms and the module's
   field list. A custom activity's form stays as designed. */
const withProfileUsername = (options, moduleData) => {
  if (!Array.isArray(options) || !moduleData || !moduleData.isUserCollection) return options;
  if (options.some((x) => x && x.fieldName === "username")) return options;
  const username = queryLogic.querySelectionPrefix.find((x) => x.id === "username");
  return username ? [...options, { ...username }] : options;
};

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-btn": FTSBtn,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-formula": FTSFormula,
    "fts-query-designer": FTSQueryDesigner,
    "fts-automation-scoped-variables": FTSAutomationScopedVariables,
    "fts-automation-contenteditable": FTSAutomationContenteditable,
    "fts-automation-badge": FTSAutomationBadge,
    "fts-spinner": FTSSpinner,
    "fts-field-design": FTSFieldDesign,
    "v-select": vSelect,
  },
  data: () => ({
    inistateLogo: commons.fetiasLogo,
    nodeData: null,
    formData: null,
    originalData: null,
    resolve: null,
    states: [],
    selectedGroup: null,
    selection: [],
    selectedAction: null,
    information: [],
    content: null,
    variables: [],
    moduleOptions: [],
    logicOptions: [],
    selectedModule: null,
    selectedActivity: null,
    selectedTemplate: null,
    classification: [],
    lastVariable: null,
    originalFieldOptions: [],
    fieldOptions: [],
    errorMessage: null,
    validating: false,
    loading: false,
  }),
  validations() {
    let formData = {};
    formData = {
      type: { required },
    };

    if (this.selectedAction && this.selectedAction.type == "plugin") {
    } else if (
      this.selectedAction &&
      this.formData.pluginType &&
      Array.isArray(this.selectedAction.form)
    ) {
      formData.payload = {};
      this.selectedAction.form.forEach((x) => {
        /* [type: formula] */
        if (x.type === "formula") {
          formData.payload[x.id] = {
            required: requiredIf((y) => !y[`${x.id}Expand`]),
          };
          formData.fields = {
            required: requiredIf((y) => y[`${x.id}Expand`]),
            $each: {
              key: {
                required: requiredIf(() => this.formData[`${x.id}Expand`]),
              },
              $trackBy: "id",
            },
          };
        }
      });
    } else if (this.selectedAction && Array.isArray(this.selectedAction.form)) {
      this.selectedAction.form.forEach((x) => {
        /* [type: formula] */
        if (x.type === "formula") {
          if (x.required) {
            formData[x.id] = { required };
          } else {
            formData[x.id] = {
              required: requiredIf((y) => !y[`${x.id}Expand`] && x.required),
            };

            formData.fields = {
              required: requiredIf((y) => y[`${x.id}Expand`]),
              $each: {
                key: {
                  required: requiredIf(() => this.formData[`${x.id}Expand`]),
                },
                $trackBy: "id",
              },
            };
          }
        } else if (x.type === "form") {
          /* [type: form] */
          const hasForm =
            this.selectedActivity && this.selectedActivity.hasForm;
          formData[x.id] = {
            required: requiredIf((y) => hasForm && !y[`${x.id}Expand`]),
          };
          formData.fields = {
            required: requiredIf((y) => hasForm && y[`${x.id}Expand`]),
            $each: {
              key: {
                required: requiredIf(
                  () => hasForm && this.formData[`${x.id}Expand`]
                ),
              },
              $trackBy: "id",
            },
          };
        } else if (x.required) {
          formData[x.id] = { required };
        }
      });
    }
    /* [type: module] moduleId */
    if (formData.moduleId) {
      formData.module = formData.moduleId;
    }
    /* [type: activity] entry */
    if (formData.activityId) {
      formData.entry = {
        required: requiredIf((x) => x.activityId != "create"),
      };
    }
    return { formData };
  },
  methods: {
    // Mechanism B1: declared trigger tokens ({{form.X}} / {{form.Table.Col}}) on the action.
    addTriggerField() {
      if (!Array.isArray(this.formData.triggerFields))
        Vue.set(this.formData, "triggerFields", []);
      this.formData.triggerFields.push("");
    },
    setTriggerField(index, value) {
      Vue.set(this.formData.triggerFields, index, value);
    },
    removeTriggerField(index) {
      this.formData.triggerFields.splice(index, 1);
    },
    async expandFields(fieldName) {
      if (this.formData[fieldName + "Expand"]) {
        Vue.set(this.formData.payload, fieldName, null);
        Vue.set(this.formData, fieldName + "Expand", false);
      } else {
        Vue.set(this.formData.payload, fieldName, {});
        Vue.set(this.formData, fieldName + "Expand", true);
      }
    },
    async show(item, variables, nodeData) {
      this.nodeData = nodeData;
      this.variables = variables;
      this.selectedAction = null;
      this.selectedModule = null;
      this.formData = _.cloneDeep(item);
      this.loading = true;

      if (this.formData.type == "plugin") {
        let project = this.groupSelections.find(
          (x) => x.id === this.formData.project
        );
        if (project) {
          this.selectedAction = project.list.find(
            (x) => x.id === this.formData.pluginType
          );
          this.selectedAction.type = "plugin";

          let pluginsResponse = await this.$store.dispatch("plugin", {
            project: project.id,
            id: this.formData.pluginType,
          });
          this.selectedAction.form = pluginsResponse.data.form;
        }
      } else if (this.formData.type) {
        this.selectedAction = automationBlocks.actionSelections.find(
          (x) => x.id === this.formData.type
        );
      } else {
        this.selectedGroup = this.groupSelections[0];
      }

      if (
        this.formData.type === 'logic' && 
        this.$store.state.canvas && 
        this.$store.state.canvas.logicServices
      ) {
        this.logicOptions = this.$store.state.canvas.logicServices.map((x) => ({ 
          id: x.id, 
          name: x.name 
        }));
      }

      return new Promise(async (resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        if (
          this.formData.module &&
          Object.prototype.toString.call(this.formData.module) ===
            "[object String]"
        ) {
          this.formData.module = parseInt(this.formData.module);
        }

        if (this.selectedAction) {
          if (this.selectedAction.variable && !this.formData.fieldName) {
            /* set fieldName */
            this.formData.fieldName = commons.generateFieldName();
          }

          if (
            this.selectedAction.id == "find" ||
            this.selectedAction.id == "findAll" ||
            this.selectedAction.id == "getForm" ||
            this.selectedAction.id == "getDocument" ||
            this.selectedAction.id == "getShare" ||
            this.selectedAction.id == "doActivity"
          ) {
            await this.loadModule();
            /* set selectedModule */
            this.selectedModule = this.moduleOptions.find(
              (x) => x.id == this.formData.module
            );
            /* set filter */
            if (
              (this.selectedAction.id == "find" ||
                this.selectedAction.id == "findAll") &&
              !this.formData.filter
            ) {
              this.$set(this.formData, "filter", {
                items: [],
              });
            }
          }

          if (this.selectedAction.id == "assign") {
            /* set fieldOptions */
            this.fieldOptions = await this.prepareFieldOptions();
          }

          if (
            this.selectedAction.id == "getForm" ||
            this.selectedAction.id == "doActivity"
          ) {
            /* set selectedActivity */
            this.selectedActivity = this.activityOptions.find(
              (x) => x.id == this.formData.activityId
            );
            /* set fieldOptions */
            if (this.selectedModule && this.selectedActivity)
              this.fieldOptions = await this.prepareFieldOptions();
          }

          if (this.selectedAction.id == "getDocument" && this.formData.templateId) {
            const findTemplate = this.templateOptions.find(x => x.id == this.formData.templateId);

            if (findTemplate) {
              this.selectedTemplate = findTemplate;
            } else {
              this.selectedTemplate = { id: this.formData.templateId };
            }
          }
        }

        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    done() {
      /* set type */
      this.formData.type = this.selectedAction && this.selectedAction.id;
      /* set type:plugin */
      if (this.selectedAction && this.selectedAction.type == "plugin") {
        this.formData.type = "plugin";
      }
      /* validations */
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid && !this.errorMessage) {
        if (this.formData) this.resolve && this.resolve(this.formData);
        this.reset();
      } else {
        console.info("Invalid form", this.$v.formData);
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    /* teammate fill scope — absent means "all fields" so the saved action stays minimal */
    setFillScopeMode(mode) {
      if (mode === "except") {
        Vue.set(this.formData, "fillScope", {
          mode: "except",
          fields:
            (this.formData.fillScope && this.formData.fillScope.fields) || [],
        });
      } else {
        this.$delete(this.formData, "fillScope");
      }
    },
    setFillScopeFields(fields) {
      Vue.set(this.formData, "fillScope", {
        mode: "except",
        fields: Array.isArray(fields) ? fields : [],
      });
    },
    /* option:selected carries the raw OPTION while option:deselected carries the already
       reduced entry (valueAsArray holds reduced values in multiple mode), so the reducer has
       to be idempotent: reduce(reduce(x)) === reduce(x). */
    reduceFillScopeField(option) {
      if (option && typeof option === "object")
        return option.displayName || option.fieldName;
      return option;
    },
    /* In multiple mode the select pushes the pick onto its bound array IN PLACE and emits no
       value (Select.vue select()), so a fillScope stored without a `fields` array - which
       binds the throwaway [] from fillScopeFields - never saw the pick at all. Commit from the
       event instead, rebuilt from the committed list and deduped so it stays right when the
       in-place push DID land on formData.fillScope.fields. */
    addFillScopeField(option) {
      const field = this.reduceFillScopeField(
        Array.isArray(option) ? option[option.length - 1] : option
      );
      if (!field) return;
      const fields = this.fillScopeFields.filter((x) => x !== field);
      fields.push(field);
      this.setFillScopeFields(fields);
    },
    removeFillScopeField(option) {
      const field = this.reduceFillScopeField(option);
      this.setFillScopeFields(this.fillScopeFields.filter((x) => x !== field));
    },
    getIconByFieldName(key, list) {
      const field = list.find((x) => x.fieldName === key);
      if (field) {
        return this.informationIcon(field.type);
      }
    },
    getName(property) {
      if (!this.fieldOptions) {
        return "";
      }

      let field = this.fieldOptions.find((x) => x.fieldName == property.key);
      if (field) return field.displayName;
      return null;
    },
    validateFormula: _.debounce(async function () {
      if (this.nodeData && this.nodeData.id) {
        this.validating = true;
        this.errorMessage = null;
        let diagram = _.cloneDeep(automationDesignStore.state.diagram);
        let node = Object.values(diagram).find(
          (x) => x.data.id == this.nodeData.id
        );
        let action = node.data.actions.find((x) => x.id == this.formData.id);

        if (!action) {
          node.data.actions.push(this.formData);
          action = this.formData;
        } else {
          Object.assign(action, this.formData);
        }

        try {
          let result = await this.$store.dispatch("validateAutomation", {
            blocks: diagram,
            validate: {
              nodeId: this.nodeData.id,
              actionId: action.id,
            },
          });

          let returnedNode = Object.values(result.data.blocks).find(
            (x) => x.data.id == this.nodeData.id
          );
          let returnedAction = returnedNode.data.actions.find(
            (x) => x.id == this.formData.id
          );
          this.$set(this.formData, "variableType", returnedAction.variableType);

          if (returnedAction.moduleId)
            this.$set(this.formData, "moduleId", returnedAction.moduleId);
          else this.$delete(this.formData, "moduleId");

          if (returnedAction.activityId)
            this.$set(this.formData, "activityId", returnedAction.activityId);
          else this.$delete(this.formData, "activityId");

          if (returnedAction.informationId)
            this.$set(
              this.formData,
              "informationId",
              returnedAction.informationId
            );
          else this.$delete(this.formData, "informationId");

          if (returnedAction.isList)
            this.$set(this.formData, "isList", returnedAction.isList);
          else this.$delete(this.formData, "isList");

          if (returnedAction.isGroup)
            this.$set(this.formData, "isGroup", returnedAction.isGroup);
          else this.$delete(this.formData, "isGroup");

          if (this.formData.type == "assign") {
            if (
              returnedAction.variableType == 35 ||
              returnedAction.variableType == 36 ||
              returnedAction.variableType == 7
            ) {
              this.fieldOptions = await this.prepareFieldOptions();
            }
          }
        } catch (err) {
          if (err.response && err.response.data && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          } else {
            this.errorMessage = "Something went wrong";
          }
        } finally {
          this.validating = false;
        }
      }
    }, 500),
    async selectPluginAction(project, action) {
      let pluginsResponse = await this.$store.dispatch("plugin", {
        project: project.id,
        id: action.id,
      });

      this.selectedAction = action;
      this.selectedAction.type = "plugin";
      this.formData.variable = true;
      if (this.formData.variable && !this.formData.fieldName) {
        this.formData.fieldName = commons.generateFieldName();
      }
      this.selectedAction.form = pluginsResponse.data.form;
      Vue.set(this.formData, "payload", {});
      this.formData.type = "plugin";
      this.formData.pluginType = action.id;
      this.formData.project = project.id;
    },
    async selectAction(action) {
      this.selectedAction = action;
      this.formData.type = action.id;
      this.loading = true;

      if (this.selectedAction) {
        if (this.selectedAction.variable && !this.formData.fieldName) {
          this.formData.fieldName = commons.generateFieldName();
        }

        if (
          this.selectedAction.id == "find" ||
          this.selectedAction.id == "findAll" ||
          this.selectedAction.id == "getForm" ||
          this.selectedAction.id == "getDocument" ||
          this.selectedAction.id == "getShare" ||
          this.selectedAction.id == "doActivity"
        ) {
          // this.moduleOptions = this.$store.state.workspace.vectors;
          await this.loadModule();

          if (
            this.selectedAction.id == "find" ||
            this.selectedAction.id == "findAll"
          ) {
            this.$set(this.formData, "variableType", 7);
            if (!this.formData.filter) {
              this.$set(this.formData, "filter", {
                items: [],
              });
            }
          } else if (this.selectedAction.id == "getForm") {
            this.$set(this.formData, "variableType", 35);
          }

          if (this.selectedAction.id == "findAll") {
            this.$set(this.formData, "isList", true);
          }
        } else if (this.selectedAction.id == "setVariable") {
          if (!this.formData.name) {
            for (var i = 0; i < 10; i++) {
              if (!this.variables.find((x) => x.name == `Variable ${i + 1}`)) {
                this.$set(this.formData, "name", `Variable ${i + 1}`);
                break;
              }
            }
          }
        } else if (this.selectedAction.id == "logic") {
          console.log(this.$store.state);

          if (this.$store.state.canvas.logicServices) {
            let options = this.$store.state.canvas.logicServices.map((x) => ({
              id: x.id,
              name: x.name,
            }));
            this.logicOptions = options;
          }
        }
      }
      this.loading = false;
    },
    setLastVariable(variable) {
      this.lastVariable = variable;
    },
    updateModule(item) {
      if (item && item.id) {
        this.selectedModule = item;
        this.formData.moduleId = item.id.toString();
      } else {
        this.selectedModule = null;
        this.formData.moduleId = null;
      }
      /* reset activity */
      this.updateActivity();
    },
    updateActivity(item) {
      if (item && item.id) {
        this.selectedActivity = item;
        if (!this.formData.activityId)
          this.$set(this.formData, "activityId", item.id);
      } else {
        this.selectedActivity = null;
        if (this.formData.activityId) this.$delete(this.formData, "activityId");
      }
    },
    updateTemplate(item) {
      if (item && item.id) {
        this.selectedTemplate = item;
        if (!this.formData.templateId)
          this.$set(this.formData, "templateId", item.id);
      } else {
        this.selectedTemplate = null;
        if (this.formData.templateId) this.$delete(this.formData, "templateId");
      }
    },

    async expandCollapseValue(field) {
      if (!this.formData[field]) {
        this.$set(this.formData, field, true);
      } else {
        this.$delete(this.formData, field);
        // to-do clear formData.valueProperties?
      }

      /* SS05000: fieldOptions starts as [], so the old `!this.fieldOptions` guard was never
         true and this lazy load never ran — leaving the key picker empty whenever the
         debounced validate had not (or could not) fill it. Reload on an empty list instead,
         and keep what we have if the reload cannot resolve a form yet. */
      if (this.formData[field] === true
        && !(this.fieldOptions && this.fieldOptions.length)) {
        const options = await this.prepareFieldOptions();
        if (Array.isArray(options)) this.fieldOptions = options;
      }
    },
    /* Find / Find all options, saved on the action as the server reads them (ModuleAutomation):
       sort = [{ fieldId, direction }] (a listing's defaultSort shape), limit, mode, whenNone. */
    setFindSort(fieldId, direction) {
      if (!fieldId) {
        this.$delete(this.formData, "sort");
        return;
      }
      this.$set(this.formData, "sort", [{ fieldId, direction: direction || 0 }]);
    },
    setFindLimit(value) {
      const limit = parseInt(value, 10);
      if (!limit) this.$delete(this.formData, "limit");
      else this.$set(this.formData, "limit", Math.max(1, Math.min(1000, limit)));
    },
    setWhenNone(mode) {
      if (mode === "skip" || mode === "error") this.$set(this.formData, "whenNone", mode);
      else this.$delete(this.formData, "whenNone");
      if (mode !== "error") this.$delete(this.formData, "whenNoneMessage");
    },
    /* Table rows, saved as TableRowsBlock reads it: mode, table, where, match ("all" or absent), fields =
       [{ column, value }]. Settings a mode does not use are dropped so the saved action stays honest. */
    tableRowsUses(setting) {
      const uses = {
        add: ["table", "fields"],
        update: ["where", "match", "fields", "whenNone"],
        remove: ["table", "where", "match", "whenNone"],
        upsert: ["table", "where", "fields"],
        lookup: ["where", "whenNone"],
      };
      return (uses[this.tableRowsMode] || []).includes(setting);
    },
    setTableRowsMode(mode) {
      this.$set(this.formData, "mode", mode);
      ["table", "where", "match", "fields", "whenNone"]
        .filter((x) => !this.tableRowsUses(x))
        .forEach((x) => this.$delete(this.formData, x));
      if (!this.tableRowsUses("whenNone")) this.$delete(this.formData, "whenNoneMessage");
    },
    setTableRowsMatch(match) {
      if (match === "all") this.$set(this.formData, "match", "all");
      else this.$delete(this.formData, "match");
    },
    addTableRowsField() {
      this.$set(this.formData, "fields", [...(this.formData.fields || []), { column: "", value: "" }]);
    },
    setTableRowsField(index, key, value) {
      const fields = (this.formData.fields || []).slice();
      fields.splice(index, 1, { ...fields[index], [key]: value });
      this.$set(this.formData, "fields", fields);
    },
    removeTableRowsField(index) {
      const fields = (this.formData.fields || []).filter((x, i) => i !== index);
      if (fields.length) this.$set(this.formData, "fields", fields);
      else this.$delete(this.formData, "fields");
    },
    /* the vendored select shows only its `text` prop for a single choice, never the picked option itself */
    optionText(option, key = "label") {
      return option ? option[key] : null;
    },
    setFindMode(mode) {
      if (mode === "count") {
        this.$set(this.formData, "mode", "count");
        this.$delete(this.formData, "sort");
        this.$delete(this.formData, "limit");
      } else {
        this.$delete(this.formData, "mode");
      }
    },
    filterOptions(options, search) {
      const keys = this.formData.fields.map((x) => x.key);
      return options.filter(
        (x) =>
          !keys.some((y) => y == x.fieldName) &&
          x.displayName &&
          x.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1
      );
    },
    async updateClassification() {
      let result = await this.$store.dispatch("loadClassification", {
        moduleId: this.formData.module,
        activityId: this.formData.activityId,
      });
      this.classification = result.data;
    },
    async setFormProperty() {
      await this.updateClassification();
      if (!this.formData.enableValueProperty) {
        this.$set(this.formData, "enableValueProperty", true);
      } else {
        this.$delete(this.formData, "enableValueProperty");
        // to-do clear formData.valueProperties?
      }
    },
    addValue() {
      if (!this.formData.fields || !Array.isArray(this.formData.fields))
        this.$set(this.formData, "fields", []);

      this.formData.fields.push({
        id: commons.nanoID(),
      });
    },
    removeValue(item) {
      if (Array.isArray(this.formData.fields)) {
        const findIndex = this.formData.fields.findIndex(
          (x) => x.id === item.id
        );
        if (findIndex > -1) this.formData.fields.splice(findIndex, 1);
      }
    },
    async prepareFieldOptions() {
      try {
        let moduleId = this.formData.module;
        if (this.formData.moduleId) {
          moduleId = this.formData.moduleId;
        }

        if (moduleId) {
          let data = await this.$store.dispatch("findVariable", {
            moduleId: moduleId,
          });
          if (this.formData.informationId) {
            const information = data.information.find(
              (x) => x.id == this.formData.informationId
            );
            return information && information.fields;
          } else if (this.formData.activityId) {
            if (Array.isArray(this.variables)) {
              const variablesForm = this.variables.find(x => x.fieldName == "form");
              if (variablesForm && variablesForm.form && variablesForm.activityId == this.formData.activityId) {
                const classification = commons.getAllElements(null, variablesForm.form);
                return Array.isArray(classification) ? classification : [];
              }
            }

            if (
              this.formData.activityId == "create" ||
              this.formData.activityId == "edit" ||
              this.formData.activityId == "createEditView"
            ) {
              return withProfileUsername(
                formDesign.getDefaultClassificationByActivityId(
                  data.standards, 
                  this.formData.activityId, 
                  data.information
                ),
                data
              );
            } else {
              return formDesign.getClassificationByActivityId(
                data.activities,
                this.formData.activityId
              );
            }
          } else if (this.formData.informationId) {
            const information = data.information.find(
              (x) => x.id == this.formData.informationId
            );
            return information && information.fields;
          } else {
            return withProfileUsername(data.information, data);
          }
        } else if (this.selectedActivity) {
          let data = await this.$store.dispatch("findVariable", {
            moduleId: this.selectedModule.id,
          });
          if (
            this.selectedActivity.id == "create" ||
            this.selectedActivity.id == "edit"
          ) {
            return withProfileUsername(
              formDesign.getDefaultClassificationByActivityId(
                data.standards, 
                this.selectedActivity.id, 
                data.information
              ),
              data
            );
          } else {
            return formDesign.getClassificationByActivityId(
              data.activities, 
              this.selectedActivity.id
            );
          }
        }
      } catch (err) {
        console.error("AutomationActionModal:PrepareFieldOptions", err);
      }
    },
    /* module */
    async loadModule() {
      try {
        const res = await this.$store.dispatch("loadVectorSelection");
        this.moduleOptions = res.data;
      } catch (err) {
        console.error("AutomationAction:LoadModule", err);
      }
    },
    /* field validator */
    fieldValidator(rowId, fieldName) {
      try {
        const validations = Object.values(this.$v.formData.fields.$each.$iter);
        return validations.find((x) => x.$model.id === rowId)[fieldName];
      } catch (err) {
        console.warn("[FieldValidatorException]", err);
      }
    },
    fieldValidatorOnInput(rowId, fieldName) {
      let fieldValidator = this.fieldValidator(rowId, fieldName);
      return fieldValidator && fieldValidator.$touch();
    },
    fieldValidatorInvalid(rowId, fieldName) {
      const fieldValidator = this.fieldValidator(rowId, fieldName);
      return (
        fieldValidator &&
        fieldValidator.$invalid &&
        (fieldValidator.$dirty || this.$v.formData.$dirty)
      );
    },
    /* standard activity icon */
    getStandardActivityIcon(id) {
      if (["create", "edit", "delete"].indexOf(id) > -1)
        return commons.standardActivityIcon(id);
    },
  },
  computed: {
    fieldKeys() {
      return this.formData.fields ? this.formData.fields.map((x) => x.key) : [];
    },
    triggerFieldRows() {
      return Array.isArray(this.formData.triggerFields) ? this.formData.triggerFields : [];
    },
    // D-8: the effective combined trigger set (formula ∪ watches ∪ declarations), derived from
    // the SyncTriggers classification the form designer publishes — never re-derived locally.
    combinedSyncFields() {
      const verdict = automationDesignStore.state.syncVerdict;
      if (!verdict || !verdict.fields || (verdict.always && verdict.always.length)) return [];
      return verdict.fields.map(field => ({
        label: field.secondary ? `${field.primary} › ${field.secondary}` : field.primary,
        reasons: (field.targets || [])
          .map(t => t.origin || t.primary)
          .filter((v, i, arr) => v && arr.indexOf(v) === i)
          .join(", "),
      }));
    },
    activityOptions() {
      let standards = [
        { id: "create", name: "Create", hasForm: true },
        { id: "edit", name: "Edit", hasForm: true },
        // { id: "changeStatus", name: "change state" },
        { id: "delete", name: "Delete" },
      ];

      if (this.selectedModule == null) return standards;

      if (this.formData.type == "doActivity") {
        return standards.concat(this.selectedModule.activities);
      }

      return standards
        .concat(this.selectedModule.activities)
        .filter((x) => x.hasForm);
    },
    templateOptions() {
      return this.selectedModule.documents;
    },
    title() {
      return !this.selectedAction
        ? "Select an action below"
        : (this.selectedAction && this.selectedAction.name) || "Action";
    },
    releaseStage() {
      if (!this.selectedAction) {
        return null;
      }
      
      const action = automationBlocks.actionSelections.find(action => action.name === this.selectedAction.name);
      return action ? action.releaseStage : null;
    },
    groupSelections() {
      return [
        { id: "basic", name: "Basic" },
        ...automationDesignStore.state.plugins,
        // {
        //   id: "essentials",
        //   name: "Essentials",
        //   selection: [
        //     {
        //       id: "http",
        //       project: "essentials",
        //       name: "HTTP",
        //       description: "Http Request to external party",
        //       variable: true,
        //       form: [
        //         {
        //           id: "name",
        //           name: "Name",
        //           type: "contenteditable",
        //           required: true,
        //         },
        //         {
        //           id: "url",
        //           fieldName: "url",
        //           type: 0,
        //           name: "Url",
        //           type: "formula",
        //         },
        //         {
        //           id: "method",
        //           fieldName: "method",
        //           type: 27,
        //           name: "Method",
        //           type: "formula",
        //         },
        //         {
        //           id: "payload",
        //           fieldName: "payload",
        //           type: 37,
        //           name: "Payload",
        //           type: "formula",
        //         },
        //       ],
        //     },
        //   ],
        // },
      ];
    },
    actionSelections() {
      return automationBlocks.actionSelections.filter(action => {
        if (action.releaseStage === ReleaseStage.ALPHA && !authorizationStore.getters.isInternal) {
          return false;
        }
        if (action.releaseStage === ReleaseStage.BETA && !authorizationStore.getters.isBeta) {
          return false;
        }

        return true;
      });
    },
    isExist() {
      if (this.nodeData && Array.isArray(this.nodeData.actions))
        return Boolean(
          this.nodeData.actions.find((x) => x.id === this.formData.id)
        );
    },
    /* teammate fill scope: 'all' (fillScope absent) or 'except' ({ mode, fields }) */
    fillScopeMode() {
      return this.formData.fillScope && this.formData.fillScope.mode === "except"
        ? "except"
        : "all";
    },
    fillScopeFields() {
      return (this.formData.fillScope && this.formData.fillScope.fields) || [];
    },
    /* the current FORM's fields, for the fill-scope exclude picker. Only a form-automation
       context carries the form variable; in a module automation this is empty and the picker
       simply offers nothing (fillScope is meaningless there anyway). */
    fillScopeOptions() {
      if (!Array.isArray(this.variables)) return [];
      const formVariable = this.variables.find((x) => x.fieldName == "form");
      if (!formVariable || !formVariable.form) return [];
      const fields = commons.getAllElements(null, formVariable.form) || [];
      return fields.filter((x) => x && (x.displayName || x.fieldName));
    },
    findModeOptions() {
      return [
        { label: "The entries", value: "rows" },
        { label: "Count only (counted on the server)", value: "count" },
      ];
    },
    findSortDirections() {
      return [
        { label: "Ascending", value: 0 },
        { label: "Descending", value: 1 },
      ];
    },
    tableRowsMode() {
      return this.formData.mode || "add";
    },
    tableRowsModeOptions() {
      return [
        { label: "Add a row", value: "add" },
        { label: "Update rows", value: "update" },
        { label: "Remove rows", value: "remove" },
        { label: "Add or update a row", value: "upsert" },
        { label: "Look up a row", value: "lookup" },
      ];
    },
    tableRowsMatchOptions() {
      return [
        { label: "The first that matches", value: "first" },
        { label: "Every row that matches", value: "all" },
      ];
    },
    seniorityOptions() {
      return [
        { label: "Junior", value: "junior" },
        { label: "Senior", value: "senior" },
        { label: "Expert", value: "expert" },
        { label: "Consultant", value: "consultant" },
      ];
    },
    whenNoneOptions() {
      return [
        { label: "Continue", value: "continue" },
        { label: "Skip the rest of this branch", value: "skip" },
        { label: "Stop with an error", value: "error" },
      ];
    },
    // the module's fields a Find can order by (the same information the listing sort picks from)
    findSortFields() {
      return ((this.selectedModule && this.selectedModule.information) || [])
        .filter((x) => x.id && x.type != 14 && x.fieldName != "$DocumentId");
    },
    findSortFieldId() {
      const sort = this.formData.sort && this.formData.sort[0];
      return sort ? sort.fieldId : null;
    },
    findSortDirection() {
      const sort = this.formData.sort && this.formData.sort[0];
      return sort && sort.direction ? 1 : 0;
    },
    querySelection() {
      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.selectedModule.userCollection ? [] : ["username"],
        ({ id }, key) => id === key
      )
        .concat(
          this.selectedModule.information.filter(
            (x) => x.fieldName != "$DocumentId"
          )
        )
        .concat(queryLogic.querySelectionSuffix);
    },
  },
  watch: {
    "formData.module": function () {
      if (this.selectedModule) {
        if (this.formData.type == "find")
          this.formData.name = this.selectedModule.name + " Found";
        else if (this.formData.type == "findAll")
          this.formData.name = this.selectedModule.name + " List Found";
      }
    },
    "formData.activityId": async function () {
      if (this.selectedModule && this.selectedActivity) {
        if (this.formData.type == "getForm")
          this.formData.name =
            this.selectedModule.name +
            " " +
            this.selectedActivity.name +
            " " +
            "Form";
        else if (this.formData.type == "doActivity")
          this.formData.name =
            this.selectedModule.name +
            " " +
            this.selectedActivity.name +
            " " +
            "Submission";

        this.fieldOptions = await this.prepareFieldOptions();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/colors" as *;

.find-sort-row {
  display: flex;
  gap: 0.5rem;
  .find-sort-field { flex: 2; min-width: 0; }
  .find-sort-direction { flex: 1; min-width: 0; }
}
.when-none-message {
  margin-top: 0.5rem;
}
.table-rows-hint {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  opacity: 0.7;
}
.table-rows-field {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  .table-rows-column { flex: 1; min-width: 0; }
  .table-rows-value { flex: 2; min-width: 0; }
}

.fts-automation-action-modal {
  /* action menu */
  .automation-action-menu-container {
    height: calc(500px + 0.25rem);
    display: flex;
    align-items: flex-start;
    padding-top: 0.25rem;
  }
  .automation-action-menu {
    flex: 1;
    max-height: 500px;
    padding: 0.5rem;
    border-radius: 0.25rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-card-background);
    .menu-item {
      display: flex;
      align-items: center;
      padding: 0.375rem 0.75rem;
      border-radius: 0.25rem;
      transition: 0.3s ease-in-out;
      cursor: pointer;
      .menu-item-icon {
        height: 2.25rem;
        width: 2.25rem;
        margin-right: 0.75rem;
        padding: 0.25rem;
        border: 1px solid var(--fts-border);
        border-radius: 0.25rem;
        background-color: var(--fts-light);
        > img {
          max-height: 100%;
          max-width: 100%;
        }
      }
      .menu-item-name {
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--fts-text);
      }
      .menu-item-description {
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
      &:hover {
        @include theme {
          background-color: rgba(
            $color: theme-get("fts-default"),
            $alpha: 0.075
          );
        }
      }
      &.menu-item--active {
        @include theme() {
          background: rgba($color: theme-get("fts-primary"), $alpha: 0.075);
        }
        .menu-item-name {
          color: var(--fts-primary);
        }
      }
    }
    &.automation-action-menu--action {
      flex: 1 0 60%;
      max-width: 60%;
      margin-left: 1rem;
      .menu-item {
        &:hover {
          @extend .menu-item--active;
        }
      }
    }
  }
  .automation-action-menu-error {
    margin-top: 1rem;
    font-size: 0.75rem;
    color: var(--fts-danger);
    text-align: right;
  }
  /* value label */
  .value-label-group {
    &:not(.multiple) {
      margin-bottom: 0.375rem;
    }
  }
  /* value property */
  .value-property-container {
    .value-property-wrapper {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1.5rem;
      > * {
        &:not(:last-child) {
          margin-right: 0.75rem;
        }
      }
      .value-property-key {
        width: 280px;
      }
      .value-property-value {
        flex: 1;
      }
    }
  }
  /* variable */
  .action-variable {
    $variable-font-size: 1rem;
    $variable-icon-font-size: 0.875rem;
    display: flex;
    align-items: flex-start;
    .action-variable-icon {
      margin-top: calc(#{$variable-font-size - $variable-icon-font-size});
      margin-right: 0.375rem;
      font-size: $variable-icon-font-size;
    }
    .action-variable-wrapper {
      min-width: 0;
    }
    .action-variable-control {
      font-size: $variable-font-size;
    }
    .action-variable-tag {
      max-width: 100%;
      display: inline-block;
      margin-top: 0.175rem;
      padding: 0.175rem 0.575rem;
      border-radius: 0.5rem;
      border: 1px solid var(--fts-block-variable-border);
      background: var(--fts-block-variable-background);
      .action-variable-tag__badge {
        font-size: 0.675rem;
      }
    }
  }
  /* validate message */
  .automation-validate-message {
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    line-height: 1.5;
    background-color: rgba(var(--fts-text-rgb), 0.09);
    color: var(--fts-text);
    &.automation-validate-message--error {
      background-color: rgba(var(--fts-danger-rgb), 0.09);
      color: var(--fts-danger);
    }
  }
  /* teammate fill scope (§4.3.1) */
  .teammate-fill-scope {
    .teammate-fill-scope-hint {
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.75;
    }
    .teammate-fill-scope-fields {
      margin-top: 0.5rem;
    }
  }
  /* declared sync trigger fields (Mechanism B1) */
  .trigger-fields-group {
    .trigger-fields-hint {
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.75;
    }
    .trigger-fields-row {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: 0.375rem;
      .trigger-fields-row__formula {
        flex: 1;
      }
    }
    .trigger-fields-combined {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.85;
      .trigger-fields-combined__chip {
        display: inline-block;
        margin: 0.125rem 0.175rem 0 0;
        padding: 0 0.375rem;
        border-radius: 0.25rem;
        background-color: rgba(var(--fts-primary-rgb), 0.12);
      }
    }
  }
}
</style>